import { useFormikContext } from 'formik';

import { Button } from '../../../../cdl/Button/Button';
import { FormikCheckbox } from '../../../../cdl/Checkbox/FormikCheckbox';
import { FormikInput } from '../../../../cdl/Input/FormikInput';
import { Text } from '../../../../cdl/Text/Text';
import { ButtonGroup } from '../../../../common/buttons/ButtonGroup';
import { FormikCustomerCompanyMultiSelect } from '../../../../common/form-elements/formik/FormikCustomerCompanyMultiSelect';
import { translate } from '../../../../common/helpers/translate.helper';
import { Box } from '../../../../common/ui/Box';

export interface EditWebhookFormState {
    url: string;
    customerIds: string[];
    resourceTypes: {
        draftEnquiry: boolean;
        enquiry: boolean;
        offer: boolean;
        approvalRequest: boolean;
    };
}

interface EditWebhookFormContentProps {
    onDismiss: () => void;
    groupId: string;
}

export const EditWebhookFormContent = ({ onDismiss, groupId }: EditWebhookFormContentProps) => {
    const { values, isValid, isSubmitting } = useFormikContext<EditWebhookFormState>();

    const hasSelectedEvents = Object.values(values.resourceTypes).some(Boolean);
    const hasSelectedCompanies = values.customerIds.length > 0;
    const isFormValid = isValid && hasSelectedEvents && hasSelectedCompanies;

    return (
        <Box display="grid" gap={16} padding={24}>
            <Text variant="title">{translate('webhooks.edit.title')}</Text>

            <FormikInput
                name="url"
                label={translate('webhooks.configureDestination.endpointUrl')}
                placeholder={translate('webhooks.configureDestination.httpsUrl')}
            />

            <Box>
                <Text variant="fieldLabel" marginBottom={8}>
                    {translate('webhooks.companies')}
                </Text>
                <FormikCustomerCompanyMultiSelect name="customerIds" groupId={groupId} />
            </Box>

            <Box marginBottom={8}>
                <Text variant="fieldLabel" color="foreground.muted">
                    {translate('webhooks.selectEvents.eventGroups')}
                </Text>
                <Box display="flex" flexDirection="column" rowGap={16}>
                    <FormikCheckbox
                        name="resourceTypes.draftEnquiry"
                        label={translate('webhooks.selectEvents.draftEnquiry.title')}
                        description={translate('webhooks.selectEvents.draftEnquiry.description')}
                    />
                    <FormikCheckbox
                        name="resourceTypes.enquiry"
                        label={translate('webhooks.selectEvents.enquiry.title')}
                        description={translate('webhooks.selectEvents.enquiry.description')}
                    />
                    <FormikCheckbox
                        name="resourceTypes.offer"
                        label={translate('webhooks.selectEvents.offer.title')}
                        description={translate('webhooks.selectEvents.offer.description')}
                    />
                    <FormikCheckbox
                        name="resourceTypes.approvalRequest"
                        label={translate('webhooks.selectEvents.approvalRequest.title')}
                        description={translate('webhooks.selectEvents.approvalRequest.description')}
                    />
                </Box>
            </Box>

            <ButtonGroup justifyContent="end">
                <Button type="button" onClick={onDismiss}>
                    {translate('btn.cancel')}
                </Button>
                <Button type="submit" emphasis="high" disabled={!isFormValid} isLoading={isSubmitting}>
                    {translate('webhooks.edit.save')}
                </Button>
            </ButtonGroup>
        </Box>
    );
};
